import { useApi } from '@src/app/context/Api';
import { ListCodesData } from '@src/entities/Code/includes/types';
import { GetEventData, GetEventRequestArgs, ListEventTypesData } from '@src/entities/Event/includes/types';
import { EventType as EventTypeComponent } from '@src/entities/Event/ui/components/EventType';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { useAssets } from '@src/shared/ui/hooks/useAssets';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Table } from '@uhg-abyss/web/ui/Table';
import { motion } from 'framer-motion';
import { isUndefined } from 'lodash';
import React, { useEffect } from 'react';

import { Skeleton } from './components/Skeleton';
import { Styles } from './includes/styles';

type Assets = {
  ListEventTypes?: {
    data?: ListEventTypesData;
  };
  ListImpactedSystems?: {
    data?: ListCodesData;
  };
};

/**
 * Page: View Event
 *
 * Provides the user with a screen to view information about the event.
 */
export function View() {
  const router = useRouter();
  const routeParams = router?.getRouteParams();
  const eventId = routeParams?.eventId;

  const { useApiQuery } = useApi('Event');

  const [GetEvent, { data: event, isFetching, isLoading }] = useApiQuery('GetEvent') as [
    (args: GetEventRequestArgs) => void,
    {
      data?: GetEventData;
      isFetching: boolean;
      isLoading: boolean;
    },
  ];

  const { assets, isLoadingAssets }: { assets: Assets; isLoadingAssets: boolean } = useAssets([
    {
      apiKey: 'Event',
      queryKey: 'ListEventTypes',
    },
    {
      apiKey: 'Source',
      queryKey: 'ListImpactedSystems',
    },
  ]);

  /**
   * Fetches the event data if the event ID is defined and the event data is not already loaded.
   */
  useEffect(() => {
    if (eventId && isUndefined(event)) {
      GetEvent({ eventId });
    }
  }, [eventId, event, GetEvent]);

  if (isLoading || isFetching || isLoadingAssets) {
    return <Skeleton />;
  }

  return (
    <ErrorHandler location="src/pages/private/Notifications/Events/View/View.tsx">
      <motion.div
        animate="open"
        initial={{ opacity: 0 }}
        variants={{
          closed: { opacity: 0 },
          open: { opacity: 1 },
        }}
      >
        <Styles>
          <Grid>
            <Grid.Col css={{ paddingTop: 'var(--abyss-space-lg)' }} span={{ xs: '100%' }}>
              <Heading offset={0}>{event?.title}</Heading>
            </Grid.Col>
            <Grid.Col span={{ xs: '50%' }}>
              <div className="static-table">
                <Table.Container title="">
                  <Table.TableBody>
                    <Table.Row>
                      <Table.Column>Event Type</Table.Column>
                      <Table.Cell>
                        <EventTypeComponent
                          eventTypeCode={event?.eventTypeCode}
                          eventTypes={assets?.ListEventTypes?.data}
                        />
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Event Date(s)</Table.Column>
                      <Table.Cell>
                        {dayjs(event?.startDate).format('MM/DD/YYYY')} - {dayjs(event?.endDate).format('MM/DD/YYYY')}
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column
                        css={
                          event?.impactedSystems && event.impactedSystems.length > 1
                            ? { verticalAlign: 'top' }
                            : { verticalAlign: 'middle' }
                        }
                      >
                        Impacted Systems
                      </Table.Column>
                      <Table.Cell>
                        <Layout.Stack alignItems="left" alignLayout="left" grow>
                          {event?.impactedSystems?.map((impactedSystem: string) => {
                            const system = assets?.ListImpactedSystems?.data?.find((asset) => {
                              return asset?.codeId === impactedSystem;
                            });
                            return <div key={system?.codeId}>{system?.codeDesc}</div>;
                          })}
                        </Layout.Stack>
                      </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Actual Records</Table.Column>
                      <Table.Cell>{Number(event?.actualRecordCount ?? 0).toLocaleString('en-US')}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Expected Records</Table.Column>
                      <Table.Cell>{Number(event?.expectedRecordCount ?? 0).toLocaleString('en-US')}</Table.Cell>
                    </Table.Row>
                  </Table.TableBody>
                </Table.Container>
              </div>
            </Grid.Col>
            <Grid.Col span={{ xs: '50%' }}>
              <div className="static-table">
                <Table.Container title="">
                  <Table.TableBody>
                    <Table.Row>
                      <Table.Column>Created By</Table.Column>
                      <Table.Cell>{event?.createdBy}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Create Date</Table.Column>
                      <Table.Cell>{dayjs(event?.createdDate).format('MM/DD/YYYY')}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Last Modified By</Table.Column>
                      <Table.Cell>{event?.lastModifiedBy}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Column>Last Modified Date</Table.Column>
                      <Table.Cell>{dayjs(event?.lastModifiedDate).format('MM/DD/YYYY')}</Table.Cell>
                    </Table.Row>
                  </Table.TableBody>
                </Table.Container>
              </div>
            </Grid.Col>
            <Grid.Col span={{ xs: '60%' }}>
              <div className="static-table">
                <Table.Container title="">
                  <Table.TableBody>
                    <Table.Row>
                      <Table.Column css={{ verticalAlign: 'top' }}>Notes</Table.Column>
                      <Table.Cell>{event?.notes}</Table.Cell>
                    </Table.Row>
                  </Table.TableBody>
                </Table.Container>
              </div>
            </Grid.Col>
          </Grid>
        </Styles>
      </motion.div>
    </ErrorHandler>
  );
}
